// Даты задач — строки YYYY-MM-DD. Date живёт только внутри функции и в местном времени:
// parseISO('2026-09-07') — местная полночь, а не UTC; так день не теряется в UTC+7.
import { addDays as dfAddDays, addMonths, addQuarters, differenceInCalendarDays, format, getDay, getISOWeek, parseISO, startOfISOWeek, startOfMonth, startOfQuarter } from 'date-fns';

export type ISODate = string;

export const toISO = (d: Date): ISODate => format(d, 'yyyy-MM-dd');
export const fromISO = (s: ISODate): Date => parseISO(s);

export const addDays = (s: ISODate, n: number): ISODate => toISO(dfAddDays(fromISO(s), n));
export const daysBetween = (a: ISODate, b: ISODate): number => differenceInCalendarDays(fromISO(b), fromISO(a));
export const today = (): ISODate => toISO(new Date());

/** Понедельник и воскресенье недели, в которую попадает дата. */
export const mondayOf = (s: ISODate): ISODate => toISO(startOfISOWeek(fromISO(s)));
export const sundayOf = (s: ISODate): ISODate => addDays(mondayOf(s), 6);

/** n недель от понедельника: конец — воскресенье. Так выглядит новая задача по умолчанию. */
export const spanWeeks = (monday: ISODate, weeks: number): { start_date: ISODate; end_date: ISODate } => ({
  start_date: monday,
  end_date: addDays(monday, 7 * Math.max(1, weeks) - 1),
});

/** Сколько недель занимает отрезок, с округлением (7 дней = 1). */
export const weeksOf = (start: ISODate, end: ISODate): number => Math.max(1, Math.round((daysBetween(start, end) + 1) / 7));

/** Пересечение отрезков дат включительно. */
export const overlaps = (aStart: ISODate, aEnd: ISODate, bStart: ISODate, bEnd: ISODate): boolean =>
  aStart <= bEnd && bStart <= aEnd;

export const dayLabel = (s: ISODate): string => format(fromISO(s), 'd');

export const monthStartOf = (s: ISODate): ISODate => toISO(startOfMonth(fromISO(s)));
export const nextMonthStart = (s: ISODate): ISODate => toISO(addMonths(startOfMonth(fromISO(s)), 1));
export const quarterStartOf = (s: ISODate): ISODate => toISO(startOfQuarter(fromISO(s)));
export const nextQuarterStart = (s: ISODate): ISODate => toISO(addQuarters(startOfQuarter(fromISO(s)), 1));
export const monthYearLabel = (s: ISODate): string => format(fromISO(s), 'MMM yyyy');
export const monthLabel = (s: ISODate): string => format(fromISO(s), 'MMM');
export const quarterLabel = (s: ISODate): string => format(fromISO(s), "'Q'Q yyyy");
export const addMonthsISO = (s: ISODate, n: number): ISODate => toISO(addMonths(fromISO(s), n));
export const longDate = (s: ISODate): string => format(fromISO(s), 'd MMM yyyy');
export const shortDate = (s: ISODate): string => format(fromISO(s), 'd MMM');
/** Период одной строкой: «7–14 Oct 2026», «7 Sep – 4 Oct 2026», «7 Dec 2026 – 4 Jan 2027». */
export function dateRange(start: ISODate, end: ISODate): string {
  if (start === end) return longDate(start);
  if (start.slice(0, 7) === end.slice(0, 7)) return `${format(fromISO(start), 'd')}–${longDate(end)}`;
  if (start.slice(0, 4) === end.slice(0, 4)) return `${shortDate(start)} – ${longDate(end)}`;
  return `${longDate(start)} – ${longDate(end)}`;
}
/** Момент в местном времени: «12 Sep, 14:03» — для истории. */
export const dateTimeLabel = (iso: string): string => format(new Date(iso), 'd MMM, HH:mm');
export const monthDay = (s: ISODate): string => format(fromISO(s), 'MM/dd');
export const isoWeek = (s: ISODate): number => getISOWeek(fromISO(s));
/** ISO-день недели: понедельник 1, воскресенье 7. */
export const isoWeekday = (s: ISODate): number => ((getDay(fromISO(s)) + 6) % 7) + 1;

/**
 * Нерабочие праздники по производственному календарю РФ — те же даты, что раньше жили событиями
 * вида holiday; на сетке красятся как выходной. 2027 — добавить, когда календарь опубликуют.
 */
export const HOLIDAYS: readonly ISODate[] = ['2026-05-01', '2026-05-11', '2026-06-12', '2026-11-04', '2026-12-31'];
